// @owners { team: patients }
import {
  CLEAR_DEEP_LINK_PATH,
  CLEAR_FETCH_SERVICE_AVAILABILITY_ERROR,
  CLEAR_REGISTRATION_NOTIFICATIONS,
  FETCH_SERVICE_AVAILABILITY_FAILED,
  FETCH_SERVICE_AVAILABILITY_LOADING,
  FETCH_SERVICE_AVAILABILITY_SUCCEEDED,
  LOGIN_FAILED,
  LOGIN_LOADING,
  LOGIN_SUCCEEDED,
  LOGOUT_FAILED,
  LOGOUT_SUCCEEDED,
  SET_POST_LOGIN_ROUTE,
  SIGNUP_FAILED,
  SIGNUP_LOADING,
  SIGNUP_SUCCEEDED,
  STORE_DEEPLINK_PATH,
  UPDATE_TEMP_SIGNUP_USER,
  VALIDATING_USER_FAILED,
  VALIDATING_USER_LOADING,
  VALIDATING_USER_SUCCEEDED,
  type clearDeepLinkPath,
  type clearFetchServiceAvailabilityError,
  type clearRegistrationNotifications,
  type completeOnboarding,
  type fetchServiceAvailabilityFailed,
  type fetchServiceAvailabilityLoading,
  type fetchServiceAvailabilitySucceeded,
  type loginFailed,
  type loginLoading,
  type loginSucceeded,
  type logoutFailed,
  type logoutSucceeded,
  type setPostLoginRoute,
  type signingUp,
  type signupFailed,
  type signupSucceeded,
  type storeDeepLinkPath,
  type updateTempSignupUser,
  type validatingUser,
  type validatingUserFailed,
  type validatingUserSucceeded,
} from '~shared/actions/auth';
import { BIOMETRICS_AUTHENTICATED, type biometricAuthenticationSuccess } from '~shared/actions/biometrics';
import { SET_PRESENCE, type setPresence } from '~shared/actions/presence';
import { COMPLETE_ONBOARDING, REHYDRATE } from '~shared/constants';
import { getPlatformOS } from '~shared/helpers/getPlatformOS';
import { type User, type UserCreateRequest } from '~shared/types';
import { type APIError } from '~shared/types/APIError';

export type AuthState = {
  loggedInAt: string | null;
  biometricsAuthenticated: boolean;
  authError: APIError | null;
  authorized: boolean;
  loggingIn: boolean;
  onboarding: boolean;
  serviceAvailabilityChecked: boolean;
  serviceAvailable: boolean;
  signUpSuccess: boolean | null;
  tempSignupUser: UserCreateRequest;
  user: User | null;
  validatingUser: boolean;
  signingUp: boolean;
  postLoginRoute: string | null;
  deepLinkPath: string | null;
};

const initialState: AuthState = {
  loggedInAt: null,
  biometricsAuthenticated: false,
  authError: null,
  authorized: false,
  loggingIn: false,
  onboarding: false,
  serviceAvailabilityChecked: false,
  serviceAvailable: false,
  signUpSuccess: null,
  tempSignupUser: {},
  // still being used to hold referred_by_id
  user: null,
  validatingUser: false,
  signingUp: false,
  postLoginRoute: null,
  deepLinkPath: null,
};

type AuthActions =
  | { type: typeof REHYDRATE; payload?: { auth?: AuthState } }
  | { type: '@@router/LOCATION_CHANGE' }
  | ReturnType<typeof loginLoading>
  | ReturnType<typeof loginSucceeded>
  | ReturnType<typeof loginFailed>
  | ReturnType<typeof updateTempSignupUser>
  | ReturnType<typeof signingUp>
  | ReturnType<typeof signupSucceeded>
  | ReturnType<typeof signupFailed>
  | ReturnType<typeof logoutSucceeded>
  | ReturnType<typeof logoutFailed>
  | ReturnType<typeof validatingUser>
  | ReturnType<typeof validatingUserSucceeded>
  | ReturnType<typeof validatingUserFailed>
  | ReturnType<typeof clearDeepLinkPath>
  | ReturnType<typeof clearRegistrationNotifications>
  | ReturnType<typeof completeOnboarding>
  | ReturnType<typeof fetchServiceAvailabilityLoading>
  | ReturnType<typeof fetchServiceAvailabilitySucceeded>
  | ReturnType<typeof fetchServiceAvailabilityFailed>
  | ReturnType<typeof clearFetchServiceAvailabilityError>
  | ReturnType<typeof setPostLoginRoute>
  | ReturnType<typeof storeDeepLinkPath>
  | ReturnType<typeof biometricAuthenticationSuccess>
  | ReturnType<typeof setPresence>;

const authReducer = (state: AuthState = initialState, action: AuthActions): AuthState => {
  switch (action.type) {
    case REHYDRATE:
      // action fired by redux-persist after rehydration. if there is persisted auth state, merge it back in
      // otherwise return the current state. after logging out, payload is undefined after clearing storage so we
      // need to handle that too. for web, we were running into a login loop issue so handling that differently here
      if (getPlatformOS() === 'web') {
        return { ...initialState, ...state };
      }

      if (action.payload && action.payload.auth) {
        return { ...state, ...action.payload.auth };
      }

      return state;
    case LOGIN_LOADING:
      return { ...state, loggingIn: true };

    case LOGIN_SUCCEEDED:
      return {
        ...state,
        authorized: true,
        authError: null,
        loggingIn: false,
        user: action.payload,
        loggedInAt: new Date().toISOString(),
      };

    case LOGIN_FAILED:
      return { ...state, authError: action.payload, loggingIn: false };

    case UPDATE_TEMP_SIGNUP_USER:
      return { ...state, tempSignupUser: { ...state.tempSignupUser, ...action.payload } };

    case SIGNUP_LOADING:
      return { ...state, signingUp: true, signUpSuccess: null };

    case SIGNUP_SUCCEEDED:
      return {
        ...state,
        loggedInAt: new Date().toISOString(),
        authError: null,
        authorized: true,
        onboarding: true,
        signingUp: false,
        signUpSuccess: true,
        user: action.payload,
      };

    case SIGNUP_FAILED:
      return { ...state, authError: action.payload, signingUp: false, signUpSuccess: false };

    case STORE_DEEPLINK_PATH:
      return { ...state, deepLinkPath: action.payload };

    case CLEAR_DEEP_LINK_PATH:
      return { ...state, deepLinkPath: null };

    case LOGOUT_SUCCEEDED:
      return initialState;

    case LOGOUT_FAILED:
      return { ...state, authError: action.payload };

    case VALIDATING_USER_LOADING:
      return { ...state, authError: null, validatingUser: true };

    case VALIDATING_USER_SUCCEEDED:
      return {
        ...state,
        authError: null,
        authorized: true,
        onboarding: true,
        user: action.payload,
        validatingUser: false,
      };

    case VALIDATING_USER_FAILED:
      return { ...state, authError: action.payload, validatingUser: false };

    case CLEAR_REGISTRATION_NOTIFICATIONS:
      return { ...state, signUpSuccess: null };

    case COMPLETE_ONBOARDING:
      return { ...state, onboarding: false };

    case FETCH_SERVICE_AVAILABILITY_LOADING:
      return state;

    case FETCH_SERVICE_AVAILABILITY_SUCCEEDED:
      return { ...state, serviceAvailabilityChecked: true, serviceAvailable: action.payload.available };

    case FETCH_SERVICE_AVAILABILITY_FAILED:
      return state;

    case CLEAR_FETCH_SERVICE_AVAILABILITY_ERROR:
      return { ...state, serviceAvailabilityChecked: false };

    case SET_POST_LOGIN_ROUTE:
      return { ...state, postLoginRoute: action.payload };

    case SET_PRESENCE:
      return { ...state, biometricsAuthenticated: false };

    case BIOMETRICS_AUTHENTICATED:
      return { ...state, biometricsAuthenticated: true };

    default:
      return state;
  }
};

export default authReducer;
